import React from 'react';

// Line drawings of where the backset is measured, one per door type. The door edge is on the
// left, the tape runs from that edge to the centre of the keyhole / spindle / hole, and the
// dimension is drawn in red. Stroke and fill colours are fixed so the drawing reads the same
// on every product page and in the guide. Sized by the parent (viewBox 320 × 220).
const INK = '#111827';
const RED = '#dc2626';
const PANEL = '#f3f4f6';

// Horizontal dimension line with arrowheads, from x1 to x2 at height y, labelled beneath.
const Dim = ({ x1, x2, y, label }) => (
  <g stroke={RED} fill={RED}>
    <line x1={x1} y1={y} x2={x2} y2={y} strokeWidth="2" />
    <polygon points={`${x1},${y} ${x1 + 8},${y - 4} ${x1 + 8},${y + 4}`} stroke="none" />
    <polygon points={`${x2},${y} ${x2 - 8},${y - 4} ${x2 - 8},${y + 4}`} stroke="none" />
    <line x1={x2} y1={y - 20} x2={x2} y2={y + 8} strokeWidth="1" strokeDasharray="3 3" />
    <text x={(x1 + x2) / 2} y={y + 20} textAnchor="middle" fontSize="13" fontWeight="700" stroke="none" fontFamily="system-ui, sans-serif">{label}</text>
  </g>
);

// The edge of the door, drawn as a thick band on the left with the door face to its right.
const DoorEdge = ({ label = 'door edge' }) => (
  <g>
    <rect x="30" y="16" width="270" height="170" rx="4" fill={PANEL} stroke={INK} strokeWidth="2" />
    <rect x="30" y="16" width="14" height="170" fill="#d1d5db" stroke={INK} strokeWidth="2" />
    <text x="37" y="104" fontSize="10" fill={INK} textAnchor="middle" transform="rotate(-90 37 104)" fontFamily="system-ui, sans-serif">{label}</text>
  </g>
);

const Hinged = () => (
  <svg viewBox="0 0 320 220" role="img" aria-label="Hinged door: the backset runs from the door edge to the centre of the keyhole" className="w-full h-auto">
    <DoorEdge />
    {/* faceplate in the edge */}
    <rect x="38" y="60" width="8" height="100" fill="#fff" stroke={INK} strokeWidth="2" />
    {/* lever */}
    <rect x="96" y="82" width="14" height="14" rx="3" fill="#fff" stroke={INK} strokeWidth="2" />
    <rect x="108" y="84" width="70" height="10" rx="5" fill="#fff" stroke={INK} strokeWidth="2" />
    {/* keyhole */}
    <circle cx="103" cy="140" r="9" fill="#fff" stroke={INK} strokeWidth="2" />
    <rect x="101" y="140" width="4" height="10" fill={INK} />
    <Dim x1={44} x2={103} y={170} label="backset" />
  </svg>
);

const Bore = () => (
  <svg viewBox="0 0 320 220" role="img" aria-label="Round-bore door: the backset runs from the door edge to the centre of the round hole" className="w-full h-auto">
    <DoorEdge />
    {/* latch faceplate */}
    <rect x="38" y="90" width="8" height="40" fill="#fff" stroke={INK} strokeWidth="2" />
    {/* the 54 mm hole */}
    <circle cx="120" cy="110" r="34" fill="#fff" stroke={INK} strokeWidth="2" strokeDasharray="6 4" />
    <circle cx="120" cy="110" r="3" fill={INK} />
    <text x="120" y="70" textAnchor="middle" fontSize="11" fill={INK} fontFamily="system-ui, sans-serif">54 mm hole</text>
    <Dim x1={44} x2={120} y={170} label="60 or 70 mm" />
  </svg>
);

const Sliding = () => (
  <svg viewBox="0 0 320 220" role="img" aria-label="Sliding door: the mortice size runs from the door edge to the centre of the hook lock keyhole" className="w-full h-auto">
    <DoorEdge label="door edge" />
    {/* narrow stile: glass starts close to the edge */}
    <rect x="130" y="16" width="170" height="170" fill="#e0f2fe" stroke={INK} strokeWidth="2" />
    <text x="215" y="104" textAnchor="middle" fontSize="11" fill="#0369a1" fontFamily="system-ui, sans-serif">glass</text>
    {/* hook lock faceplate + hook */}
    <rect x="38" y="50" width="8" height="120" fill="#fff" stroke={INK} strokeWidth="2" />
    <path d="M30 96 h-10 a8 8 0 0 0 0 16 h10" fill="none" stroke={INK} strokeWidth="2" />
    {/* keyhole on the stile */}
    <circle cx="82" cy="110" r="8" fill="#fff" stroke={INK} strokeWidth="2" />
    <rect x="80" y="110" width="4" height="9" fill={INK} />
    <Dim x1={44} x2={82} y={170} label="28 or 35 mm" />
  </svg>
);

const Blank = () => (
  <svg viewBox="0 0 320 220" role="img" aria-label="Blank aluminium door: measure the flat stile beside the glass and take about half of it" className="w-full h-auto">
    <DoorEdge />
    <rect x="150" y="16" width="150" height="170" fill="#e0f2fe" stroke={INK} strokeWidth="2" />
    <text x="225" y="104" textAnchor="middle" fontSize="11" fill="#0369a1" fontFamily="system-ui, sans-serif">glass</text>
    <text x="97" y="60" textAnchor="middle" fontSize="11" fill={INK} fontFamily="system-ui, sans-serif">flat stile</text>
    {/* the future lock body must sit on the flat stile */}
    <rect x="72" y="70" width="44" height="70" rx="4" fill="none" stroke={INK} strokeWidth="2" strokeDasharray="5 4" />
    <circle cx="94" cy="120" r="3" fill={INK} />
    <g stroke={INK} fill={INK}>
      <line x1="44" y1="156" x2="150" y2="156" strokeWidth="1.5" />
      <polygon points="44,156 52,152 52,160" stroke="none" />
      <polygon points="150,156 142,152 142,160" stroke="none" />
      <text x="97" y="151" textAnchor="middle" fontSize="11" stroke="none" fontFamily="system-ui, sans-serif">stile width</text>
    </g>
    <Dim x1={44} x2={94} y={178} label="about half" />
  </svg>
);

const DIAGRAMS = { hinged: Hinged, bore: Bore, sliding: Sliding, blank: Blank };

export default function BacksetDiagram({ kind = 'hinged', className = '' }) {
  const D = DIAGRAMS[kind] || Hinged;
  return (
    <div className={className} data-testid={`backset-diagram-${kind}`}>
      <D />
    </div>
  );
}
